import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { Button } from '../../components';
import { Tags } from '../../components/shared';
import { articlesService } from '../../services';
import GradualBlur from '../../components/GradualBlur';
import './ArticleDetail.css';

export const ArticleDetail = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const articleData = await articlesService.getArticleById(id);
        setArticle(articleData);
      } catch (error) {
        console.error('Error fetching article:', error);
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id]);

  const formatDate = (article) => {
    if (article.publishDate) {
      const [year, month, day] = article.publishDate.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    }
    if (article.createdAt) {
      return article.createdAt.toDate().toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    }
    return '';
  };

  const estimateReadTime = (content) => {
    if (!content) return '1 min read';
    const wordsPerMinute = 200;
    const wordCount = content.split(' ').length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Article URL copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="article-detail-container">
        <div className="container">
          <div className="loading-container">
            <p data-cursor-text>Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-detail-container">
        <div className="container">
          <div className="error-container">
            <h2 data-cursor-text>Article Not Found</h2>
            <p data-cursor-text>The article you're looking for doesn't exist or has been removed.</p>
            <Button as={Link} to="/articles">
              <ArrowLeft size={20} />
              <span data-cursor-text>Back to Articles</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="article-detail-container">
      <div className="container">
        <Button as={Link} to="/articles" variant="ghost" className="back-button">
          <ArrowLeft size={20} />
          <span data-cursor-text>Back to Articles</span>
        </Button>

        {article.featuredImage && (
          <div
            className="article-image"
            style={{ backgroundImage: `url(${article.featuredImage})` }}
          />
        )}

        <header className="article-header">
          <h1 className="article-title" data-cursor-text>{article.title}</h1>

          {article.excerpt && (
            <p className="article-excerpt" data-cursor-text>{article.excerpt}</p>
          )}

          <div className="article-meta">
            <Tags
              tags={article.tags}
              variant="article"
              maxTags={10}
              showMore={false}
            />
            <div className="article-meta-info">
              <div className="article-meta-item">
                <Calendar size={14} />
                <span data-cursor-text>{formatDate(article)}</span>
              </div>
              <div className="article-meta-item">
                <Clock size={14} />
                <span data-cursor-text>{estimateReadTime(article.content)}</span>
              </div>
            </div>
          </div>
        </header>

        <article className="article-content" style={{ paddingBottom: '12rem' }}>
          <div dangerouslySetInnerHTML={{ __html: article.content }} />
        </article>
      </div>

      <GradualBlur
        target="page"
        position="bottom"
        height="8rem"
        strength={2}
        divCount={5}
        curve="bezier"
        exponential={true}
        opacity={1}
        className="content-page-blur"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100vw',
          zIndex: 999
        }}
      />
    </div>
  );
};